import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StreamPlayer from "../components/StreamPlayer";
import ControlPanel from "../components/ControlPanel";
import StatsDisplay from "../components/StatsDisplay";
import TranscriptPanel from "../components/TranscriptPanel";
import AudioDebugPanel from "../components/AudioDebugPanel";
import LogsPanel from "../components/LogsPanel";
import FileTransferPanel from "../components/FileTransferPanel";
import ShellBrowserPanel from "../components/ShellBrowserPanel";
import SkillsPanel from "../components/SkillsPanel";
import ChatPanel from "../components/ChatPanel";
import { useChatAgent } from "../hooks/useChatAgent";
import { useWebRTC } from "../hooks/useWebRTC";
import { useGladiaTranscription } from "../hooks/useGladiaTranscription";
import { apiFetch, startSession, getSessionToken, uploadThumbnail } from "../services/api";
import { useInactivityDetector } from "../hooks/useInactivityDetector";
import { createLogger } from "../lib/logger";
import { NAV_HEIGHT } from "../components/NavBar";
import { isSessionDetail } from "../types/session";
import type { SessionDetail, SessionState } from "../types/session";
import type { InputEvent, VmResolution } from "../types/input";
import type { CommandMessage } from "../types/commands";

const slog = createLogger("session");

// ─── Constants ────────────────────────────────────────────────────────

const INITIAL_VM_RESOLUTION: VmResolution = { width: 1920, height: 1080 };

/** Parse "WIDTHxHEIGHT" string to VmResolution. */
function parseResolution(s: string | undefined): VmResolution | null {
  if (!s) return null;
  const m = s.match(/(\d+)x(\d+)/);
  if (!m || !m[1] || !m[2]) return null;
  return { width: parseInt(m[1], 10), height: parseInt(m[2], 10) };
}

/**
 * Task 200 — outcome of a `set_resolution` round trip.
 *
 * `confirmed` is the only resolution allowed to drive the pointer mapping: a
 * request that the VM refused (missing xrandr, invalid dimensions, readback
 * mismatch…) must never move the mouse reference frame, otherwise the cursor
 * desynchronises from a video that never changed size.
 */
export interface ResolutionOutcome {
  confirmed: VmResolution;
  changing: boolean;
  error: string | null;
}

const RESOLUTION_ERROR_LABELS: Record<string, string> = {
  invalid_dimensions: "dimensions refusées par la VM",
  xrandr_missing: "outil xrandr absent de la VM",
  apply_failed: "changement refusé par le serveur X",
  verify_failed: "résolution non appliquée (relecture divergente)",
  timeout: "la VM n'a pas répondu à temps",
};

const positiveDimension = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;

/** Effective resolution carried by a `resolution_changed` notification. */
function effectiveResolution(msg: Record<string, unknown>): VmResolution | null {
  const width = positiveDimension(msg.effective_width) ?? positiveDimension(msg.width);
  const height = positiveDimension(msg.effective_height) ?? positiveDimension(msg.height);
  return width !== null && height !== null ? { width, height } : null;
}

/**
 * Consume a `resolution_changed` notification.
 *
 * Only `success === true` confirms a new resolution; every other answer keeps
 * the last confirmed one and surfaces the failure.
 */
export function applyResolutionNotification(
  confirmed: VmResolution,
  msg: Record<string, unknown>,
): ResolutionOutcome {
  if (msg.success === true) {
    return { confirmed: effectiveResolution(msg) ?? confirmed, changing: false, error: null };
  }
  const requested = positiveDimension(msg.width) !== null && positiveDimension(msg.height) !== null
    ? `${msg.width}x${msg.height}`
    : "demandée";
  const code = typeof msg.error_code === "string" ? msg.error_code : "apply_failed";
  const reason = RESOLUTION_ERROR_LABELS[code] ?? code;
  return {
    confirmed,
    changing: false,
    error: `Résolution ${requested} non appliquée : ${reason}. Affichage conservé en ${confirmed.width}x${confirmed.height}.`,
  };
}

/** No answer within the freeze window: keep the last confirmed resolution. */
export function applyResolutionTimeout(confirmed: VmResolution): ResolutionOutcome {
  return {
    confirmed,
    changing: false,
    error: `Aucune confirmation de la VM : affichage conservé en ${confirmed.width}x${confirmed.height}.`,
  };
}

/**
 * Stats-based confirmation: the encoded frame size is direct evidence that the
 * change happened, and covers a lost notification.
 */
export function applyResolutionStats(
  confirmed: VmResolution,
  pending: string | null,
  statsResolution: string | null | undefined,
): ResolutionOutcome | null {
  if (!pending || !statsResolution || statsResolution !== pending) return null;
  return { confirmed: parseResolution(statsResolution) ?? confirmed, changing: false, error: null };
}

/** Detect mobile viewport (iPhone, small Android). */
const isMobileViewport = (): boolean =>
  typeof window !== "undefined" && window.innerWidth < 768;

const STATE_LABELS: Record<SessionState, { label: string; color: string }> = {
  off: { label: "Off", color: "#6b7280" },
  starting: { label: "Starting", color: "#fbbf24" },
  on: { label: "On", color: "#34d399" },
  connecting: { label: "Connecting", color: "#67e8f9" },
  ongoing: { label: "Ongoing", color: "#60a5fa" },
  stopping: { label: "Stopping", color: "#7dd3fc" },
  destroying: { label: "Destroying", color: "#f87171" },
};

// ─── Polling interval for session state ───────────────────────────────

const POLL_INTERVAL_MS = 2000;

// ─── Props ────────────────────────────────────────────────────────────

interface SessionViewProps {
  sessionId: string;
  /** When true, this session outputs audio to speakers. */
  isFocused: boolean;
  /** Called when the session is destroyed (remove from manager). */
  onRemove?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────

export default function SessionView({
  sessionId,
  isFocused,
  onRemove,
}: SessionViewProps) {
  const navigate = useNavigate();

  // Session state
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Session token — from sessionStorage (immediate) or fetched from backend (after reload)
  const [sessionToken, setSessionToken] = useState(
    () => sessionStorage.getItem(`session_token_${sessionId}`) ?? "",
  );

  // Fetch token from backend if not in sessionStorage (e.g. after page reload)
  useEffect(() => {
    if (sessionToken || !sessionId) return;
    getSessionToken(sessionId).then((token) => {
      if (token) {
        setSessionToken(token);
        sessionStorage.setItem(`session_token_${sessionId}`, token);
      }
    });
  }, [sessionId, sessionToken]);

  const [showStats, setShowStats] = useState(false);
  const [thawing, setThawing] = useState(false);
  const [showTakeover, setShowTakeover] = useState(false);
  const [takeoverChecked, setTakeoverChecked] = useState(false);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const [showDebug, setShowDebug] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showChat, setShowChat] = useState(false);
  // Task 205: shell-browser panel. Task 215 moved its mount above the stream
  // row; the toggle, its state and its handler are unchanged.
  const [showShell, setShowShell] = useState(false);
  const [showSkills, setShowSkills] = useState(false);
  // Task 175: resume trigger for the chat hook. It must be passed to useChatAgent here, but its real
  // implementation needs `fetchSession` (declared further below) — so a STABLE wrapper reads the impl
  // from a ref, wired in an effect once fetchSession exists. This avoids a temporal-dead-zone
  // reference to fetchSession in a dependency array (it is defined after this line).
  const ensureSessionOnRef = useRef<() => void>(() => {});
  const ensureSessionOn = useCallback(() => { ensureSessionOnRef.current(); }, []);
  const { messages: chatMessages, isLoading: chatLoading, error: chatError, phase: chatPhase, iteration: chatIteration, budgetExhausted: chatBudgetExhausted, continueRun: chatContinue, sendMessage: chatSend, clearMessages: chatClear, cancel: chatCancel, activeModel: chatActiveModel, agentMode: chatAgentMode, generateSummary: chatGenerateSummary, downloadFile: chatDownloadFile, summaryFile: chatSummaryFile, summaryLoading: chatSummaryLoading } = useChatAgent(sessionId, session?.state ?? null, ensureSessionOn);
  // The Chat can change Skills: the panel re-reads jobs and catalogue after each reply.
  const [chatReplies, setChatReplies] = useState(0);
  const chatWasLoading = useRef(false);
  useEffect(() => { if (chatWasLoading.current && !chatLoading) setChatReplies(n => n + 1); chatWasLoading.current = chatLoading; }, [chatLoading]);
  const [transcriptLang, setTranscriptLang] = useState("automatic");
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);

  // BUG-01: Track actual VM resolution (from session data + resolution commands)
  const [vmResolution, setVmResolution] = useState<VmResolution>(INITIAL_VM_RESOLUTION);

  // BUG-02: Pointer lock state (FPS games)
  const [pointerLockEnabled, setPointerLockEnabled] = useState(false);
  const [isPointerLocked, setIsPointerLocked] = useState(false);
  const isPointerLockedRef = useRef(false);

  // BUG-13: Resolution change freeze
  const [resolutionChanging, setResolutionChanging] = useState(false);
  // Task 200: last resolution failure, shown to the user instead of silently
  // keeping an optimistic value the VM never applied.
  const [resolutionError, setResolutionError] = useState<string | null>(null);
  const resolutionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingResolutionRef = useRef<string | null>(null);

  // BUG-13 / task 200: consume resolution_changed honestly — `success` decides.
  const handleBackendMessage = useCallback((msg: Record<string, unknown>) => {
    if (msg.type === "resolution_changed") {
      setVmResolution((current) => {
        const outcome = applyResolutionNotification(current, msg);
        setResolutionError(outcome.error);
        return outcome.confirmed;
      });
      setResolutionChanging(false);
      pendingResolutionRef.current = null;
      if (resolutionTimerRef.current) {
        clearTimeout(resolutionTimerRef.current);
        resolutionTimerRef.current = null;
      }
    }
  }, []);

  // F5: Check for active session takeover
  useEffect(() => {
    if (session === null) return;
    if (!["on", "connecting", "ongoing"].includes(session.state)) {
      setTakeoverChecked(false);
      setShowTakeover(false);
      return;
    }
    // Decide before opening signaling. Once admitted, an ongoing poll is our
    // own connection, not a new request to take it away from another viewer.
    if (takeoverChecked) return;
    if (session.state === "ongoing" && session.connected_from) {
      setShowTakeover(true);
    } else {
      setTakeoverChecked(true);
      setShowTakeover(false);
    }
  }, [session?.state, session?.connected_from, takeoverChecked]);

  // WebRTC connection — only connect when session is ready/active
  // F5: Block connection until takeover is confirmed
  const shouldConnect =
    session !== null &&
    (session.state === "on" || session.state === "ongoing" || session.state === "connecting") &&
    sessionToken !== "" &&
    takeoverChecked &&
    !showTakeover;
  const {
    stream,
    connectionState,
    stats,
    disconnect: webrtcDisconnect,
    sendMessage,
    sendInput,
    signalingState,
    vmWaiting,
  } = useWebRTC({
    sessionId: shouldConnect ? sessionId! : "",
    sessionToken: shouldConnect ? sessionToken : "",
    onBackendMessage: handleBackendMessage,
    // `stopping|off|destroying` est terminal : aucune reconnexion (tâche 201).
    sessionState: session?.state ?? null,
  });

  // Gladia live transcription via Y-Mixer (stream audio track → Gladia)
  // Uses WebRTC stream directly (not captureStream on video) so audio
  // capture works even when video.muted=true.
  // IMPORTANT: startTranscript MUST be called from user gesture (onClick),
  // not from useEffect, because AudioContext.resume() requires it.
  const {
    transcripts,
    currentPartial,
    isListening: transcriptListening,
    error: transcriptError,
    start: startTranscript,
    stop: stopTranscript,
    startMonitor,
    setSpeakersMuted,
    setSpeakerVolume,
    enabled: transcriptEnabled,
    debugInfo: transcriptDebug,
    monitoring: audioMonitoring,
    analyserInput,
    analyserSpeakers,
    analyserGladia,
  } = useGladiaTranscription(
    stream,
    transcriptLang,
    sessionId ?? "",
    session?.context_id ?? null,
    sessionToken,
    // Éligibilité de capture lue sur l'état MÉTIER et l'état FERMÉ de la
    // signalisation — plus jamais déduite d'un `MediaStream` résiduel.
    { sessionState: session?.state ?? null, signalingState },
  );

  // ── BUG-01: Parse VM resolution from session ready_info ────────────

  useEffect(() => {
    const parsed = parseResolution(session?.ready_info?.display_resolution);
    if (parsed) {
      setVmResolution(parsed);
    }
  }, [session?.ready_info?.display_resolution]);

  // ── BUG-13: Clear resolution freeze when stats match new resolution ─

  useEffect(() => {
    if (resolutionChanging && stats.resolution && pendingResolutionRef.current) {
      // Task 200: the encoded frame size is effective evidence — it confirms
      // the resolution even if the agent notification never arrived.
      setVmResolution((current) => {
        const outcome = applyResolutionStats(
          current,
          pendingResolutionRef.current,
          stats.resolution,
        );
        if (!outcome) return current;
        setResolutionError(null);
        return outcome.confirmed;
      });
      if (stats.resolution === pendingResolutionRef.current) {
        setResolutionChanging(false);
        pendingResolutionRef.current = null;
        if (resolutionTimerRef.current) {
          clearTimeout(resolutionTimerRef.current);
          resolutionTimerRef.current = null;
        }
      }
    }
  }, [stats.resolution, resolutionChanging]);

  // ── BUG-02: Pointer lock change handler ───────────────────────────

  const handlePointerLockChange = useCallback((locked: boolean) => {
    setIsPointerLocked(locked);
    isPointerLockedRef.current = locked;
  }, []);

  // ── Update session state to "active" when WebRTC connects ─────────

  useEffect(() => {
    if (connectionState === "connected" && session?.state === "on") {
      setSession((prev) =>
        prev ? { ...prev, state: "ongoing" as const } : prev,
      );
    }
  }, [connectionState, session?.state]);

  // ── Ctrl+V on the streaming page → navigate VM browser ───────────
  // Captures global paste events. If clipboard contains a URL and focus
  // is not in an input field, opens a one-shot CDP WebSocket and sends
  // a cdp_navigate command. Skips when typing into chat, files, etc.
  useEffect(() => {
    if (!sessionId || !sessionToken || connectionState !== "connected") return;

    const sendNavigate = (url: string) => {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${proto}//${window.location.host}/api/v1/sessions/${sessionId}/cdp?token=${encodeURIComponent(sessionToken)}`;
      slog.info("paste→navigate: opening CDP WS", { url, sessionId });
      const ws = new WebSocket(wsUrl);
      const requestId = crypto.randomUUID();
      const closeSafely = () => { try { ws.close(); } catch { /* ignore */ } };
      ws.onopen = () => {
        slog.info("paste→navigate: WS open, sending cdp_navigate", { url, requestId });
        ws.send(JSON.stringify({
          action: "cdp",
          request_id: requestId,
          tool: "cdp_navigate",
          args: { url },
          ts: new Date().toISOString(),
        }));
      };
      ws.onmessage = (ev) => {
        try {
          const resp = JSON.parse(String(ev.data));
          if (resp.request_id === requestId) {
            slog.info("paste→navigate: CDP response", { ok: !resp.error, status: resp.status });
            closeSafely();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => { slog.warn("paste→navigate: WS error"); closeSafely(); };
      window.setTimeout(closeSafely, 5000);
    };

    const tryNavigateFromText = (raw: string | null | undefined): boolean => {
      const text = (raw ?? "").trim();
      if (!text) { slog.info("paste: empty clipboard"); return false; }
      let url: string | null = null;
      if (/^https?:\/\//i.test(text)) {
        // URL complète collée telle quelle.
        url = text;
      } else if (
        // Domaine nu sans schéma : « google.com », « www.x.com », « youtube.com/watch?v=… ».
        // Un ou plusieurs labels + un point, suivi de rien / d'un chemin / port / query,
        // et aucune espace dans la chaîne (sinon c'est du texte, pas une URL).
        !/\s/.test(text) &&
        /^[a-z0-9-]+(\.[a-z0-9-]+)+([:/?#].*)?$/i.test(text)
      ) {
        url = "https://" + text;
      }
      if (url === null) {
        slog.info("paste: clipboard not a URL", { preview: text.slice(0, 60) });
        return false;
      }
      sendNavigate(url);
      return true;
    };

    // Path 1: real paste event (works when focus is in a paste-capable element)
    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        slog.info("paste: skipped (editable target)", { tag });
        return;
      }
      const text = e.clipboardData?.getData("text") ?? "";
      slog.info("paste event fired", { tag, len: text.length });
      if (tryNavigateFromText(text)) e.preventDefault();
    };

    // Path 2: Ctrl/Cmd+V on the video (which is NOT a paste target in Chrome,
    // so no `paste` event ever fires — we have to read the clipboard ourselves).
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "v") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        slog.info("Ctrl+V: skipped (editable target)", { tag });
        return;
      }
      slog.info("Ctrl+V intercepted, reading clipboard", { tag });
      e.preventDefault();
      if (!navigator.clipboard?.readText) {
        slog.warn("clipboard.readText not available");
        return;
      }
      navigator.clipboard.readText()
        .then(tryNavigateFromText)
        .catch((err) => slog.warn("clipboard.readText rejected", { error: String(err) }));
    };

    window.addEventListener("paste", handlePaste);
    window.addEventListener("keydown", handleKeyDown, true); // capture to beat useInputCapture
    return () => {
      window.removeEventListener("paste", handlePaste);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [sessionId, sessionToken, connectionState]);

  // ── Browser tab visibility (Page Visibility API) ─────────────────
  // Fires when user switches between browser tabs.
  // Does NOT fire on window blur (clicking address bar, notifications, etc.)
  // which would be too aggressive and break streaming.
  const [tabVisible, setTabVisible] = useState(!document.hidden);

  useEffect(() => {
    const handler = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  // ── Audio focus: speakers ON only when tab visible + session focused ─
  // Combines browser tab visibility AND in-app session focus.
  // Gladia keeps running in background (WebRTC stream track unaffected).
  const shouldPlayAudio = tabVisible && isFocused;

  useEffect(() => {
    if (videoEl) {
      if (audioMonitoring) {
        videoEl.muted = true;
      } else {
        videoEl.muted = !shouldPlayAudio;
      }
    }
  }, [videoEl, audioMonitoring, shouldPlayAudio]);

  useEffect(() => {
    if (audioMonitoring) {
      setSpeakersMuted(!shouldPlayAudio);
    }
  }, [shouldPlayAudio, audioMonitoring, setSpeakersMuted]);

  // ── Fetch session details ─────────────────────────────────────────

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await apiFetch(`/sessions/${sessionId}`);
      if (!res.ok) {
        setLoadError(`Failed to load session: ${res.status}`);
        return;
      }
      const data: unknown = await res.json();
      if (!isSessionDetail(data)) {
        setLoadError("Invalid session data");
        return;
      }
      setSession(data);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Network error");
    }
  }, [sessionId]);

  // ── Poll session state (only while "creating" → detect "ready") ────

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    // Poll during transient states: starting, stopping, connecting
    // Stop when stable: off, on, ongoing, destroying
    const TRANSIENT: SessionState[] = ["starting", "stopping", "connecting"];
    if (session !== null && !TRANSIENT.includes(session.state)) {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = setInterval(fetchSession, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [session?.state, fetchSession]);

  // ── Input event handler ───────────────────────────────────────────

  const handleInputEvent = useCallback(
    (event: InputEvent) => {
      // Translate compact InputEvent to input-daemon JSON format
      let msg: Record<string, unknown> | null = null;
      const locked = isPointerLockedRef.current;
      switch (event.t) {
        case "mm":
          msg = { type: "mousemove", x: event.x, y: event.y };
          break;
        case "mr":
          // BUG-02: Relative mouse movement (pointer lock / FPS mode)
          msg = { type: "mousemove_relative", dx: event.dx, dy: event.dy };
          break;
        case "md":
          msg = locked
            ? { type: "mousedown", button: (event.btn ?? 0) + 1 }
            : { type: "mousedown", x: event.x, y: event.y, button: (event.btn ?? 0) + 1 };
          break;
        case "mu":
          msg = locked
            ? { type: "mouseup", button: (event.btn ?? 0) + 1 }
            : { type: "mouseup", x: event.x, y: event.y, button: (event.btn ?? 0) + 1 };
          break;
        case "dc":
          msg = locked
            ? { type: "dblclick", button: (event.btn ?? 0) + 1 }
            : { type: "dblclick", x: event.x, y: event.y, button: (event.btn ?? 0) + 1 };
          break;
        case "kd":
          msg = { type: "keydown", key: event.key, code: event.code };
          break;
        case "ku":
          msg = { type: "keyup", key: event.key, code: event.code };
          break;
        case "wh":
          msg = locked
            ? { type: "scroll", deltaX: event.dx, deltaY: event.dy }
            : { type: "scroll", x: event.x, y: event.y, deltaX: event.dx, deltaY: event.dy };
          break;
      }
      if (msg) {
        const payload = JSON.stringify({ type: "input", event: msg });
        sendInput(payload);
        // Debug log (keep last 8 entries, skip mousemove flood)
        if (msg.type !== "mousemove" && msg.type !== "mousemove_relative") {
          setDebugLog((prev) => [
            ...prev.slice(-7),
            `${new Date().toLocaleTimeString()} TX: ${JSON.stringify(msg)}`,
          ]);
        }
      }
    },
    [sendInput],
  );

  // ── Auto-set mobile resolution on connect ──────────────────────────

  const mobileResApplied = useRef(false);
  useEffect(() => {
    if (
      connectionState === "connected" &&
      isMobileViewport() &&
      !mobileResApplied.current
    ) {
      mobileResApplied.current = true;
      // Lower resolution for mobile — makes text readable
      const cmd = JSON.stringify({
        type: "command",
        command: { cmd: "set_resolution", width: 1280, height: 720 },
      });
      sendMessage(cmd);
    }
  }, [connectionState, sendMessage]);

  // ── Command handler (ControlPanel → DataChannel) ──────────────────

  const handleCommand = useCallback(
    (cmd: CommandMessage) => {
      // BUG-01: Track resolution changes for coordinate mapping
      // BUG-13: Freeze video during resolution change
      if (cmd.cmd === "set_resolution") {
        // Task 200: never apply the request optimistically — vmResolution feeds
        // the pointer mapping and must only follow a confirmed change.
        setResolutionChanging(true);
        setResolutionError(null);
        pendingResolutionRef.current = `${cmd.width}x${cmd.height}`;
        if (resolutionTimerRef.current) clearTimeout(resolutionTimerRef.current);
        resolutionTimerRef.current = setTimeout(() => {
          setVmResolution((current) => {
            const outcome = applyResolutionTimeout(current);
            setResolutionError(outcome.error);
            return outcome.confirmed;
          });
          setResolutionChanging(false);
          pendingResolutionRef.current = null;
          resolutionTimerRef.current = null;
        }, 3000);
      }
      // Volume: route to Y-Mixer speaker gain (Gladia always gets full audio)
      if (cmd.cmd === "set_volume") {
        setSpeakerVolume(cmd.volume);
        setDebugLog((prev) => [
          ...prev.slice(-7),
          `${new Date().toLocaleTimeString()} VOL: ${Math.round(cmd.volume * 100)}% (Y-Mixer speakers)`,
        ]);
        return; // Don't send to VM — keep PulseAudio at 100%
      }
      const payload = JSON.stringify({ type: "command", command: cmd });
      sendMessage(payload);
      setDebugLog((prev) => [
        ...prev.slice(-7),
        `${new Date().toLocaleTimeString()} CMD: ${JSON.stringify(cmd)}`,
      ]);
    },
    [sendMessage, setSpeakerVolume],
  );

  // ── Screenshot capture (thumbnail for session card) ────────────

  const captureScreenshot = useCallback(() => {
    if (!videoEl || !sessionId) return;
    // Don't capture if video has no frames (would produce a black image
    // that overwrites a useful older thumbnail)
    if (videoEl.readyState < 2 || videoEl.videoWidth === 0) return;
    try {
      const canvas = document.createElement("canvas");
      // Thumbnail: 160x90 (16:9)
      const tw = 160;
      const th = Math.round((videoEl.videoHeight / videoEl.videoWidth) * tw) || 90;
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(videoEl, 0, 0, tw, th);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
      // Send to backend (fire-and-forget)
      uploadThumbnail(sessionId, dataUrl);
    } catch {
      // Canvas tainted or no video — skip silently
    }
  }, [videoEl, sessionId]);

  // Capture screenshot when video starts playing (first frame available)
  useEffect(() => {
    if (!videoEl) return;
    const onPlaying = () => captureScreenshot();
    videoEl.addEventListener("playing", onPlaying);
    return () => videoEl.removeEventListener("playing", onPlaying);
  }, [videoEl, captureScreenshot]);

  // Periodic screenshot every 30s while streaming
  useEffect(() => {
    if (connectionState !== "connected" || !videoEl) return;
    const interval = setInterval(captureScreenshot, 30000);
    return () => clearInterval(interval);
  }, [connectionState, videoEl, captureScreenshot]);

  // Capture screenshot on unmount (back arrow, browser back, etc.)
  const captureRef = useRef(captureScreenshot);
  captureRef.current = captureScreenshot;
  useEffect(() => {
    return () => { captureRef.current(); };
  }, []);

  // ── Disconnect ────────────────────────────────────────────────────

  const handleDisconnect = useCallback(() => {
    captureScreenshot();
    webrtcDisconnect();
    onRemove?.();
    navigate("/sessions");
  }, [navigate, webrtcDisconnect, onRemove, captureScreenshot]);

  // ── Destroy session ───────────────────────────────────────────────

  const handleDestroy = useCallback(async () => {
    if (!sessionId) return;
    try {
      await apiFetch(`/sessions/${sessionId}`, { method: "DELETE" });
      // Clean up stored session token
      sessionStorage.removeItem(`session_token_${sessionId}`);
    } catch {
      // Best effort — navigate away regardless
    }
    onRemove?.();
    navigate("/sessions");
  }, [sessionId, navigate, onRemove]);

  // ── Start (restart from off) ────────────────────────────────────

  const handleStartFromView = useCallback(async () => {
    if (!sessionId || thawing) return;
    setThawing(true);
    try {
      await startSession(sessionId);
      fetchSession();
    } catch (e) {
      slog.error("start error", { error: String(e) }, (e as Error)?.stack);
    } finally {
      setThawing(false);
    }
  }, [sessionId, fetchSession, thawing]);

  // Task 175: wire the chat hook's resume trigger now that fetchSession exists. Same pair as
  // handleStartFromView (startSession + fetchSession) — which flips the state into the transient
  // range and restarts the existing SessionView state-poll — WITHOUT the overlay button's `thawing`
  // state (that belongs to the button). handleStartFromView / the overlay button stay unchanged.
  useEffect(() => {
    ensureSessionOnRef.current = () => {
      void (async () => {
        try {
          await startSession(sessionId);
          fetchSession();
        } catch (e) {
          slog.error("resume error", { error: String(e) }, (e as Error)?.stack);
        }
      })();
    };
  }, [sessionId, fetchSession]);

  // ── Auto-stop warning (backend handles actual 360s stop) ─────────

  const { isWarning, remainingSeconds, resetTimer: resetInactivityTimer } =
    useInactivityDetector({
      timeoutMs: 5 * 60 * 1000,
      warningMs: 30 * 1000,
      enabled:
        session !== null &&
        session.state === "ongoing" &&
        connectionState === "connected" &&
        tabVisible,
      onFreeze: handleDisconnect,
    });

  // ── Render ────────────────────────────────────────────────────────

  if (loadError !== null) {
    return (
      <div
        style={{
          maxWidth: 600,
          margin: "48px auto",
          padding: 24,
          textAlign: "center",
          color: "#e5e7eb",
        }}
      >
        <div
          role="alert"
          style={{
            background: "#7f1d1d",
            color: "#fca5a5",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {loadError}
        </div>
        <button
          onClick={() => navigate("/sessions")}
          style={{
            background: "#374151",
            color: "#e5e7eb",
            border: "none",
            borderRadius: 6,
            padding: "8px 16px",
            cursor: "pointer",
          }}
        >
          Back to Sessions
        </button>
      </div>
    );
  }

  if (session === null) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: `calc(100vh - ${NAV_HEIGHT}px)`,
          color: "#9ca3af",
        }}
      >
        Loading session...
      </div>
    );
  }

  const stateInfo = STATE_LABELS[session.state] ?? STATE_LABELS.off;
  const isConnectable = ["on", "ongoing", "connecting", "off"]
    .includes(session.state);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#e5e7eb",
      }}
    >
      {/* Header bar (BUG-07: z-index above video content) */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          background: "#111",
          borderBottom: "1px solid #222",
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 8,
          zIndex: 20,
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button
            onClick={() => navigate("/sessions")}
            aria-label="Back to sessions"
            style={{
              background: "none",
              color: "#9ca3af",
              border: "none",
              cursor: "pointer",
              fontSize: 16,
              padding: "2px 4px",
            }}
          >
            ←
          </button>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Session{" "}
            <span
              style={{ fontFamily: "monospace", color: "#93c5fd" }}
              title={session.session_id}
            >
              {session.session_id.length > 12
                ? `${session.session_id.slice(0, 8)}...`
                : session.session_id}
            </span>
          </span>
          <span
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 9999,
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              background: stateInfo.color,
              color: "#111",
            }}
          >
            {stateInfo.label}
          </span>
        </div>

        {/* Controls — inline in header (BUG-08: show disabled during CREATING) */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
          <ControlPanel
            onCommand={handleCommand}
            disabled={!isConnectable || connectionState !== "connected"}
          />
        </div>

        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => setShowChat(s => !s)}
            style={{
              background: showChat ? "#f59e0b" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Chat
          </button>
          {transcriptEnabled && (
            <button
              onClick={() => {
                setShowTranscript((prev) => {
                  const next = !prev;
                  // Auto-start transcription when opening the panel
                  if (next && !transcriptListening) {
                    startTranscript();
                  }
                  return next;
                });
              }}
              style={{
                background: showTranscript ? "#4c1d95" : "#333",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "4px 10px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Transcript
            </button>
          )}
          {/* BUG-02: Pointer lock toggle for FPS games */}
          <button
            onClick={() => {
              const next = !pointerLockEnabled;
              setPointerLockEnabled(next);
              // Exit pointer lock when disabling
              if (!next && document.pointerLockElement) {
                document.exitPointerLock();
              }
            }}
            title={isPointerLocked ? "Mouse captured (ESC to release)" : "Enable mouse capture for FPS games"}
            style={{
              background: isPointerLocked ? "#dc2626" : pointerLockEnabled ? "#059669" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {isPointerLocked ? "Mouse Locked" : "Mouse Lock"}
          </button>
          <button
            onClick={() => setShowDebug((s) => !s)}
            style={{
              background: showDebug ? "#7c3aed" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Debug
          </button>
          <button
            onClick={() => setShowStats((s) => !s)}
            style={{
              background: showStats ? "#2563eb" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Stats
          </button>
          <button
            onClick={() => setShowLogs((s) => !s)}
            style={{
              background: showLogs ? "#10b981" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Logs
          </button>
          <button
            onClick={() => setShowFiles((s) => !s)}
            style={{
              background: showFiles ? "#f97316" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Files
          </button>
          <button
            onClick={() => setShowShell((s) => !s)}
            style={{
              background: showShell ? "#0ea5e9" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Shell
          </button>
          <button onClick={() => setShowSkills(value => !value)} aria-pressed={showSkills}
            style={{ background: showSkills ? "#1d4ed8" : "#333", color: "#e5e7eb", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
            Skills
          </button>
          <button
            onClick={handleDisconnect}
            aria-label="Disconnect from session"
            style={{
              background: "#374151",
              color: "#e5e7eb",
              border: "none",
              borderRadius: 6,
              padding: "4px 12px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Disconnect
          </button>
          {session.state === "off" && (
            <button
              onClick={handleDestroy}
              aria-label="Destroy session and VM"
              style={{
                background: "#991b1b",
                color: "#fca5a5",
                border: "none",
                borderRadius: 6,
                padding: "4px 12px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Destroy
            </button>
          )}
        </div>
      </header>

      {/* Starting state — waiting screen */}
      {session.state === "starting" && (
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 12,
            color: "#9ca3af",
          }}
        >
          <div style={{ fontSize: 16 }}>VM is starting up...</div>
          <div style={{ fontSize: 12 }}>
            The session will transition to On once the VM is booted.
          </div>
        </div>
      )}

      {/* Stopping state — waiting screen */}
      {session.state === "stopping" && (
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 12,
            color: "#9ca3af",
          }}
        >
          <div style={{ fontSize: 16 }}>Stopping session...</div>
          <div style={{ fontSize: 12 }}>
            The VM is shutting down.
          </div>
        </div>
      )}

      {/* Ready/Active — player area (row: video left, transcript right) */}
      {isConnectable && (
        <>
          {/* Shell-browser — pleine largeur, SOUS la barre d'outils et AU-DESSUS
              du flux (task 215). Un seul point de montage : l'ancien, sous
              LogsPanel, a ete retire. `flexShrink: 0` empeche le panneau de
              recouvrir la video quand il est deplie. */}
          {showSkills && session.context_id && (
            <SkillsPanel sessionId={sessionId} contextId={session.context_id} sessionToken={sessionToken} onClose={() => setShowSkills(false)} refreshKey={chatReplies}
              onAskChat={text => { setShowChat(true); void chatSend(text); }} />
          )}
          {showShell && sessionId && (
            <ShellBrowserPanel
              sessionId={sessionId}
              sessionToken={sessionToken}
              onClose={() => setShowShell(false)}
            />
          )}
          <div style={{ flex: 1, display: "flex", flexDirection: "row", overflow: "hidden", minHeight: 0 }}>
            {showChat && (
              <div style={{ width: "30%", minWidth: 280, maxWidth: 480, flexShrink: 0, overflow: "hidden" }}>
                <ChatPanel
                  messages={chatMessages}
                  isLoading={chatLoading}
                  error={chatError}
                  phase={chatPhase}
                  iteration={chatIteration}
                  budgetExhausted={chatBudgetExhausted}
                  onContinue={chatContinue}
                  onSend={chatSend}
                  onClear={chatClear}
                  onCancel={chatCancel}
                  onClose={() => setShowChat(false)}
                  activeModel={chatActiveModel}
                  agentMode={chatAgentMode}
                  onGenerateSummary={chatGenerateSummary}
                  onDownloadFile={chatDownloadFile}
                  summaryFile={chatSummaryFile}
                  summaryLoading={chatSummaryLoading}
                />
              </div>
            )}
            <div style={{ flex: 1, position: "relative", overflow: "hidden", minHeight: 0 }}>
              <StreamPlayer
                stream={stream}
                connectionState={connectionState}
                stats={stats}
                showStats={showStats}
                onInputEvent={handleInputEvent}
                vmResolution={vmResolution}
                onVideoRef={setVideoEl}
                pointerLockEnabled={pointerLockEnabled}
                onPointerLockChange={handlePointerLockChange}
                resolutionChanging={resolutionChanging}
                thumbnailUrl={session?.thumbnail}
                vmWaiting={vmWaiting}
              />
              {/* Task 200: a refused resolution must be visible, not silent. */}
              {resolutionError && (
                <div
                  role="alert"
                  onClick={() => setResolutionError(null)}
                  style={{
                    position: "absolute",
                    top: 12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    maxWidth: "90%",
                    padding: "8px 14px",
                    borderRadius: 6,
                    background: "rgba(120, 30, 30, 0.92)",
                    color: "#fff",
                    fontSize: 13,
                    cursor: "pointer",
                    zIndex: 20,
                  }}
                >
                  {resolutionError}
                </div>
              )}
              <StatsDisplay
                stats={stats}
                connectionState={connectionState}
                visible={showStats}
              />
              {/* F5: Takeover dialog */}
              {showTakeover && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0, 10, 30, 0.80)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 16,
                    zIndex: 12,
                  }}
                >
                  <div style={{ color: "#fcd34d", fontSize: 16, fontWeight: 600, textAlign: "center" }}>
                    A stream is already in progress
                    {session?.connected_from ? ` from ${session.connected_from}` : ""}.
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: 13 }}>
                    Taking over will disconnect the other session.
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      onClick={() => {
                        setShowTakeover(false);
                        setTakeoverChecked(true);
                      }}
                      style={{
                        background: "#2563eb",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 20px",
                        fontSize: 14,
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      Take over
                    </button>
                    <button
                      onClick={() => navigate("/sessions")}
                      style={{
                        background: "#374151",
                        color: "#e5e7eb",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 20px",
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {/* Overlay off — last video frame visible underneath */}
              {session.state === "off" && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0, 10, 30, 0.65)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 16,
                    zIndex: 10,
                  }}
                >
                  <div style={{ color: "#bae6fd", fontSize: 16, fontWeight: 600 }}>
                    {thawing
                      ? "Starting..."
                      : "Session stopped"}
                  </div>
                  {session.state === "off" && (
                    <button
                      onClick={handleStartFromView}
                      disabled={thawing}
                      style={{
                        background: thawing ? "#374151" : "#2563eb",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 20px",
                        fontSize: 14,
                        cursor: thawing ? "not-allowed" : "pointer",
                      }}
                    >
                      {thawing ? "Starting..." : "Connect"}
                    </button>
                  )}
                </div>
              )}
              {/* Warning inactivity — 30s before auto-stop */}
              {isWarning && session.state === "ongoing" && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(20, 10, 0, 0.70)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 16,
                    zIndex: 11,
                  }}
                >
                  <div style={{ color: "#fcd34d", fontSize: 18, fontWeight: 600 }}>
                    Session inactive — Stop in {remainingSeconds}s
                  </div>
                  <button
                    onClick={resetInactivityTimer}
                    style={{
                      background: "#059669",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 20px",
                      fontSize: 14,
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Stay active
                  </button>
                </div>
              )}
            </div>

            {/* Transcript panel — right sidebar */}
            {showTranscript && transcriptEnabled && (
              <TranscriptPanel
                sessionId={sessionId}
                transcripts={transcripts}
                currentPartial={currentPartial}
                isListening={transcriptListening}
                error={transcriptError}
                enabled={transcriptEnabled}
                onStart={startTranscript}
                onStop={stopTranscript}
                debugInfo={transcriptDebug}
                language={transcriptLang}
                onLanguageChange={setTranscriptLang}
              />
            )}
            {/* Files panel — right sidebar (T114) */}
            {showFiles && sessionId && (
              <FileTransferPanel
                sessionId={sessionId}
                sessionToken={sessionToken}
                onClose={() => setShowFiles(false)}
              />
            )}
          </div>

          {/* Debug panel — split: left=input events, right=audio waveforms */}
          {showDebug && (
            <AudioDebugPanel
              debugLog={debugLog}
              connectionState={connectionState}
              analyserInput={analyserInput}
              analyserSpeakers={analyserSpeakers}
              analyserGladia={analyserGladia}
              monitoring={audioMonitoring}
              onStartMonitor={startMonitor}
            />
          )}
          <LogsPanel visible={showLogs} onClose={() => setShowLogs(false)} />
        </>
      )}

      {/* Destroying */}
      {session.state === "destroying" && (
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 12,
            color: "#9ca3af",
          }}
        >
          <div style={{ fontSize: 16 }}>
            Session {session.state === "destroying" ? "shutting down" : "ended"}.
          </div>
          <button
            onClick={() => navigate("/sessions")}
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 16px",
              cursor: "pointer",
            }}
          >
            Back to Sessions
          </button>
        </div>
      )}
    </div>
  );
}
